import {
  Button,
  Col,
  Divider,
  List,
  notification,
  Row,
  Table,
  UploadFile,
} from '@pankod/refine-antd';
import { useNavigation } from '@pankod/refine-core';
import { UploadChangeParam } from 'antd/lib/upload';
import { DownloadCsvTemplateCard, ImportCsvDataCard } from 'components';
import { useState } from 'react';
import { importWarehouse } from 'services/warehouse';
import { readCSV } from 'utilities/file';
import { findDuplicate } from 'utilities/shared';

export const WarehouseCsvColumns = {
  Name: 'Name',
  Code: 'Code',
  Email: 'Email',
  PhoneNumber: 'Phone Number',
  Street: 'Street',
  Area: 'Area',
  City: 'City',
  Country: 'Country',
  Zip: 'Zip',
} as const;

interface IImportWarehouseData {
  Name: string;
  Code: string;
  Email: string;
  'Phone Number': string;
  Street: string;
  Area: string;
  City: string;
  Country: string;
  Zip: string;
}

export const ImportWarehouses: React.FC<{ redirection?: boolean }> = ({ redirection = true }) => {
  const [parsedCSVData, setParsedCSVData] = useState<IImportWarehouseData[]>([]);
  const [isButtonLoading, setIsButtonLoading] = useState<boolean>(false);

  const { list } = useNavigation();

  const onFileUploadChange = async (info: UploadChangeParam<UploadFile<any>>) => {
    try {
      if (!info.fileList.length) {
        return;
      }

      let rowNumber = 0;
      const file = info.fileList[0].originFileObj!;
      const data: IImportWarehouseData[] = await readCSV({
        file,
        transformFunction: (row: Record<string, any>) => {
          const columns = Object.keys(row).map((key) => key);

          rowNumber += 1;
          if (rowNumber === 1) {
            // throw error on missing required columns
            [
              WarehouseCsvColumns.Name,
              WarehouseCsvColumns.Code,
              WarehouseCsvColumns.Email,
              WarehouseCsvColumns.PhoneNumber,
              WarehouseCsvColumns.Street,
              WarehouseCsvColumns.Area,
              WarehouseCsvColumns.City,
              WarehouseCsvColumns.Country,
              WarehouseCsvColumns.Zip,
            ].forEach((requiredColumn, idx) => {
              const column = columns[idx];
              if (requiredColumn !== column) {
                throw new Error(
                  `Expected '${requiredColumn}' column at column ${idx + 1} but got '${column}'`,
                );
              }
            });
          }

          const requiredColumns = Object.values(WarehouseCsvColumns);
          for (const col of requiredColumns) {
            if (!row[col]) {
              throw new Error(`'${col}' has missing values in row ${rowNumber}`);
            }
          }

          const transformedRow = {
            [WarehouseCsvColumns.Name]: row[WarehouseCsvColumns.Name],
            [WarehouseCsvColumns.Code]: row[WarehouseCsvColumns.Code],
            [WarehouseCsvColumns.Email]: row[WarehouseCsvColumns.Email],
            [WarehouseCsvColumns.PhoneNumber]: row[WarehouseCsvColumns.PhoneNumber],
            [WarehouseCsvColumns.Street]: row[WarehouseCsvColumns.Street],
            [WarehouseCsvColumns.Area]: row[WarehouseCsvColumns.Area],
            [WarehouseCsvColumns.City]: row[WarehouseCsvColumns.City],
            [WarehouseCsvColumns.Country]: row[WarehouseCsvColumns.Country],
            [WarehouseCsvColumns.Zip]: row[WarehouseCsvColumns.Zip],
          };

          return transformedRow;
        },
      });

      const duplicateCode = findDuplicate(data, WarehouseCsvColumns.Code);
      const uniqueDuplicates = Array.from(new Set(duplicateCode));
      if (duplicateCode.length) {
        notification.error({
          message: 'Failed',
          description: `Found ${
            duplicateCode.length
          } duplicate values for 'Name' - ${uniqueDuplicates.join(', ')}`,
        });
        return;
      }

      setParsedCSVData(data);
    } catch (err) {
      if (err instanceof Error) {
        notification.error({
          message: 'Failed',
          description: err.message,
        });
      }
    }
  };

  const onFileRemove = (_file: UploadFile<any>) => {
    setParsedCSVData([]);
  };

  const onSubmitButtonClick = async () => {
    setIsButtonLoading(true);
    try {
      await importWarehouse(
        parsedCSVData.map((row) => ({
          name: row[WarehouseCsvColumns.Name],
          email: row[WarehouseCsvColumns.Email],
          warehouseCode: row[WarehouseCsvColumns.Code],
          mobileNumber: row[WarehouseCsvColumns.PhoneNumber],
          address: {
            street: row[WarehouseCsvColumns.Street],
            area: row[WarehouseCsvColumns.Area],
            city: row[WarehouseCsvColumns.City],
            country: row[WarehouseCsvColumns.Country],
            zip: row[WarehouseCsvColumns.Zip],
          },
        })),
      );
      if (redirection) list('warehouses');
      notification.success({
        message: 'Success',
        description: 'Successfully imported warehouses',
      });
    } catch (err) {
      if (err instanceof Error) {
        notification.error({
          message: 'Failed',
          description: err.message,
        });
      }
    }
    setIsButtonLoading(false);
  };

  const getCsvRowsForTemplate = (): string[][] => {
    const rows: string[][] = [
      [
        WarehouseCsvColumns.Name,
        WarehouseCsvColumns.Code,
        WarehouseCsvColumns.Email,
        WarehouseCsvColumns.PhoneNumber,
        WarehouseCsvColumns.Street,
        WarehouseCsvColumns.Area,
        WarehouseCsvColumns.City,
        WarehouseCsvColumns.Country,
        WarehouseCsvColumns.Zip,
      ],
    ];

    const result = [
      {
        [WarehouseCsvColumns.Name]: 'Main Warehouse',
        [WarehouseCsvColumns.Code]: 'WH-001',
        [WarehouseCsvColumns.Email]: 'main@company.com',
        [WarehouseCsvColumns.PhoneNumber]: '+8801700000001',
        [WarehouseCsvColumns.Street]: '12 Industrial Road',
        [WarehouseCsvColumns.Area]: 'Tejgaon',
        [WarehouseCsvColumns.City]: 'Dhaka',
        [WarehouseCsvColumns.Country]: 'Bangladesh',
        [WarehouseCsvColumns.Zip]: '1208',
      },
      {
        [WarehouseCsvColumns.Name]: 'Backup Warehouse',
        [WarehouseCsvColumns.Code]: 'WH-002',
        [WarehouseCsvColumns.Email]: 'backup@company.com',
        [WarehouseCsvColumns.PhoneNumber]: '+8801700000002',
        [WarehouseCsvColumns.Street]: '45 Storage Street',
        [WarehouseCsvColumns.Area]: 'Uttara',
        [WarehouseCsvColumns.City]: 'Dhaka',
        [WarehouseCsvColumns.Country]: 'Bangladesh',
        [WarehouseCsvColumns.Zip]: '1230',
      },
    ];

    result.forEach((elem) => {
      rows.push([
        elem.Name,
        elem.Code,
        elem.Email,
        elem[WarehouseCsvColumns.PhoneNumber],
        elem.Street,
        elem.Area,
        elem.City,
        elem.Country,
        elem.Zip,
      ]);
    });

    return rows;
  };

  const flatParsedCsvData = parsedCSVData.map(
    ({ Name, Email, Code, 'Phone Number': mobileNumber, Street, Area, City, Country, Zip }) => {
      const flatObject: Record<string, string | undefined> = {
        key: Code,
        Name,
        Email,
        Code,
        [WarehouseCsvColumns.PhoneNumber]: mobileNumber,
        Street,
        Area,
        City,
        Country,
        Zip,
      };

      return flatObject;
    },
  );

  const flatCsvObject = flatParsedCsvData[0] || {};
  const columns = [];
  for (const [key] of Object.entries(flatCsvObject)) {
    if (key === 'key') {
      continue;
    }
    columns.push({ title: key, key, dataIndex: key });
  }

  return (
    <List title="Import Warehouses">
      <Row gutter={[48, 16]} wrap style={{ marginBottom: '2rem' }}>
        <Col xs={11} lg={8}>
          <DownloadCsvTemplateCard
            rows={getCsvRowsForTemplate()}
            title="Download .csv template for import"
          />
        </Col>

        <Col xs={11} lg={8}>
          <ImportCsvDataCard onFileUploadChange={onFileUploadChange} onFileRemove={onFileRemove} />
        </Col>
      </Row>

      <Divider />
      {/* CSV Data Table */}
      <Row wrap={false}>
        <Col flex="auto">
          <Table columns={columns} dataSource={flatParsedCsvData} scroll={{ x: true }} />
        </Col>
      </Row>
      {/* Submit Button */}
      <Row justify="center" align="middle" style={{ marginTop: '1rem' }}>
        <Button
          style={{ width: '300px' }}
          loading={isButtonLoading}
          disabled={!parsedCSVData.length}
          onClick={onSubmitButtonClick}
        >
          Save
        </Button>
      </Row>
    </List>
  );
};
